import type { SelectItemType } from '@/lib/base/forms/select/select';
import { getApiBaseUrl } from '@/lib/config';
import type { WebhookSecretCreatedData } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import {
  mintEntitySecret,
  webhookAuthMethodToSecretFormat,
} from '@/lib/crm/secrets/service';

/** Auth methods that mint a secret on creation. `none` is excluded. */
export const SECRET_AUTH_METHODS = new Set([
  'bearer',
  'segment',
  'standard_webhooks',
]);

export const WEBHOOK_AUTH_METHOD_ITEMS: SelectItemType[] = [
  {
    id: 'bearer',
    label: 'Bearer token',
    description:
      'Validate requests using a static Bearer token in the Authorization header.',
  },
  {
    id: 'segment',
    label: 'Segment',
    description:
      "Validate requests using Segment's HMAC-SHA1 X-Signature header; deduplicates on Segment messageId.",
  },
  {
    id: 'standard_webhooks',
    label: 'Standard Webhooks',
    description:
      'Validate requests using the Standard Webhooks signature scheme (HMAC-SHA256).',
  },
  {
    id: 'none',
    label: 'None',
    description:
      'No authentication required. All incoming requests are accepted, subject to a stricter shared per-IP rate limit.',
  },
];

export const WEBHOOK_SECRET_INSTRUCTIONS: Record<
  'bearer' | 'segment' | 'standard_webhooks',
  string
> = {
  bearer: 'Send it as `Authorization: Bearer <token>`.',
  segment: "Paste it into your Segment destination's Shared Secret field.",
  standard_webhooks: 'Use it as the signing secret.',
};

export function buildWebhookPublicUrl(slug: string): string {
  return `${getApiBaseUrl()}/api/public/automations/webhooks/${slug}/`;
}

export interface CreateInboundWebhookParams {
  orgId: number;
  userId: number;
  displayName: string;
  authMethod: string;
}

export async function createInboundWebhook(
  params: CreateInboundWebhookParams
): Promise<string> {
  const { orgId, userId, displayName, authMethod } = params;
  const entityId = crypto.randomUUID();

  const builder = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(userId),
  });
  builder.objectCreated(entityId, 'ahoy_automation_webhook', {
    ahoy_display_name: displayName,
    ahoy_webhook_auth_method: authMethod,
    ahoy_webhook_enabled: 'true',
  });
  await createEventsClient(orgId).submitEvents(builder.build());

  return entityId;
}

export async function mintWebhookSecret(
  orgId: number,
  userId: number,
  entityId: string,
  authMethod: string
): Promise<WebhookSecretCreatedData> {
  const { secretEntityId, token, masked_hint } = await mintEntitySecret(
    orgId,
    userId,
    entityId,
    'ahoy_automation_webhook',
    {
      secretEntityType: 'ahoy_automation_webhook_secret',
      associationType: 'ahoy_webhook_to_secret',
      // Format follows the webhook's auth method: `standard_webhooks` REQUIRES a
      // whsec_-prefixed std-base64 secret (the inbound verifier base64-decodes it),
      // while `bearer` compares the opaque token verbatim. A hardcoded 'token' here
      // produced Standard Webhooks endpoints whose signatures could never verify.
      format: webhookAuthMethodToSecretFormat(authMethod),
    }
  );
  return {
    bearer_token: token,
    secret_hint: masked_hint,
    secret_entity_id: secretEntityId,
  };
}

export interface SetInboundWebhookEnabledParams {
  orgId: number;
  userId: number;
  entityId: string;
  enabled: string;
  startSeq: number;
}

export async function setInboundWebhookEnabled(
  params: SetInboundWebhookEnabledParams
): Promise<void> {
  const { orgId, userId, entityId, enabled, startSeq } = params;
  const builder = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(userId),
    startSeq,
  });
  builder.propertyUpdated(
    entityId,
    'ahoy_automation_webhook',
    'ahoy_webhook_enabled',
    enabled
  );
  await createEventsClient(orgId).submitEvents(builder.build());
}
